import React from 'react';
import { Modal } from '@mui/material';
import { WindowWrapper } from '../foundation/layout/modal-wrapper';
import { SaveTradingTemplateForm } from '../features/trading-templates/save-trading-template-form';
import { FileDownloadForm } from '../widgets/file-download/file-download-form.component';
import { TradingForm } from '../widgets/trading-form/trading-form.component';
import { Stats } from '../foundation/shared/components/stats';
import { AddExchangeForm } from '../features/settings/api-management/add-exchange-form';
import { ManageTradingTemplates } from '../features/trading-templates/manage-trading-templates';
import { SaveTradingLayoutForm } from '@client/features/trading-layouts/save-trading-layout-form';
import { ManageTradingLayouts } from '@client/features/trading-layouts/manage-trading-layouts';
import { ManageWorkspaceLayouts } from '@client/features/workspace-layouts/manage-workspace-layouts';
import { ConfirmationDialog } from '@client/foundation/shared/components/confirmation-dialog';
import { IndicatorTemplateEditor } from '@client/features/builders/indicator-template-editor';
import { StrategyTemplateEditor } from '@client/features/builders/strategy-template-editor';
import { SignalBlockTemplateEditor } from '@client/features/builders/signal-block-template-editor';
import type { WindowPayload, WindowProps, WindowType } from './window.types';
import { WINDOW_SIZE_PRESETS } from './window-size-presets';

type DialogWindowType = 'ConfirmationDialog';
type DialogWindowProps = Extract<WindowProps, { type: DialogWindowType }>;
type ModalWindowProps = Exclude<WindowProps, DialogWindowProps>;
type ModalWindowType = ModalWindowProps['type'];

const isDialogWindowType = (type: WindowType): type is DialogWindowType => {
  return type === 'ConfirmationDialog';
};

type CommonRendererProps = {
  type: WindowType;
  open: boolean;
  closeWindow: (type: WindowType) => void;
};

type WindowRendererProps = {
  type: WindowType;
  payload: WindowPayload;
  closeWindow: (type: WindowType) => void;
};

const DialogWindowRenderer: React.FC<
  CommonRendererProps & {
    type: 'ConfirmationDialog';
    windowProps: DialogWindowProps;
  }
> = ({
  type,
  open,
  windowProps,
  closeWindow
}) => {
  return (
    <ConfirmationDialog
      open={open}
      {...windowProps.componentProps}
      onCancel={() => {
        windowProps.componentProps.onCancel?.();
        closeWindow(type);
      }}
      onConfirm={() => {
        windowProps.componentProps.onConfirm();
        closeWindow(type);
      }}
    />
  );
};

const renderModalWindow = (windowProps: ModalWindowProps) => {
  switch (windowProps.type) {
    case 'SaveTradingTemplateForm':
      return <SaveTradingTemplateForm {...windowProps.componentProps} />;
    case 'ManageTradingTemplates':
      return <ManageTradingTemplates {...windowProps.componentProps} />;
    case 'SaveTradingLayoutForm':
      return <SaveTradingLayoutForm {...windowProps.componentProps} />;
    case 'ManageTradingLayouts':
      return <ManageTradingLayouts {...windowProps.componentProps} />;
    case 'ManageWorkspaceLayouts':
      return <ManageWorkspaceLayouts {...windowProps.componentProps} />;
    case 'FileDownloadForm':
      return <FileDownloadForm {...windowProps.componentProps} />;
    case 'TradingForm':
      return <TradingForm {...windowProps.componentProps} />;
    case 'Stats':
      return <Stats {...windowProps.componentProps} />;
    case 'AddExchangeForm':
      return <AddExchangeForm {...windowProps.componentProps} />;
    case 'IndicatorTemplateEditor':
      return <IndicatorTemplateEditor {...windowProps.componentProps} />;
    case 'StrategyTemplateEditor':
      return <StrategyTemplateEditor {...windowProps.componentProps} />;
    case 'SignalBlockTemplateEditor':
      return <SignalBlockTemplateEditor {...windowProps.componentProps} />;
  }
};

const ModalWindowRenderer: React.FC<
  CommonRendererProps & { type: ModalWindowType; windowProps: ModalWindowProps }
> = ({ type, open, windowProps, closeWindow }) => {
  const presetSx =
    WINDOW_SIZE_PRESETS[windowProps.sizePreset ?? 'default'];
  const overrideSx =
    windowProps.sx &&
    !Array.isArray(windowProps.sx) &&
    typeof windowProps.sx !== 'function'
      ? windowProps.sx
      : {};

  return (
    <Modal open={open} onClose={() => closeWindow(type)}>
      <WindowWrapper sx={{ ...presetSx, ...overrideSx }}>
        {renderModalWindow(windowProps)}
      </WindowWrapper>
    </Modal>
  );
};

export const WindowRenderer: React.FC<WindowRendererProps> = ({
  type,
  payload,
  closeWindow
}) => {
  const open = payload.open;
  const windowProps = payload.windowProps;

  if (isDialogWindowType(type)) {
    if (!windowProps || windowProps.type !== 'ConfirmationDialog') {
      return null;
    }

    return (
      <DialogWindowRenderer
        type={type}
        open={open}
        windowProps={windowProps}
        closeWindow={closeWindow}
      />
    );
  }

  if (!windowProps || windowProps.type === 'ConfirmationDialog') {
    return null;
  }

  return (
    <ModalWindowRenderer
      type={type}
      open={open}
      windowProps={windowProps}
      closeWindow={closeWindow}
    />
  );
};
